import { useMemo, useState } from 'react'
import { CalendarDays, Info, Search, Users } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, PageHeader, StatCard, StatusBadge,
} from '@/components/common'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useMyTeam } from '../api'

/**
 * The people who report to the signed-in person.
 *
 * <p>The reporting line is resolved from assignments as of today, so a transfer shows on the
 * day it takes effect rather than whenever somebody remembers to update a list. Attendance
 * that has not been processed yet says so instead of appearing as an absence — a manager
 * looking at this before the nightly run should not see their whole team marked away.
 */
export function MyTeamPage() {
  useBreadcrumb()
  const team = useMyTeam()

  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<'all' | 'direct'>('all')

  const rows = useMemo(() => {
    const members = team.data?.members ?? []
    const term = search.trim().toLowerCase()
    return members
      .filter((member) => scope === 'all' || member.directReport)
      .filter((member) => term === ''
        || (member.displayName ?? '').toLowerCase().includes(term)
        || (member.employeeCode ?? '').toLowerCase().includes(term))
  }, [team.data, search, scope])

  const offToday = (team.data?.members ?? []).filter(
    (member) => member.todayStatus === 'LEAVE' || member.todayStatus === 'ABSENT').length

  return (
    <div className="space-y-4">
      <PageHeader
        title="My team"
        subtitle="Everybody in your reporting line, as of today."
      />

      {team.isPending && <Skeleton className="h-48" />}
      {team.isError && <ErrorState error={team.error} onRetry={() => void team.refetch()} />}

      {team.isSuccess && team.data.note && (
        <EmptyState title="Nobody reports to you" description={team.data.note} />
      )}

      {team.isSuccess && !team.data.note && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              label="Direct reports"
              value={String(team.data.directReports)}
              icon={Users}
              caption="People whose assignment names you as manager"
            />
            <StatCard
              label="In your line"
              value={String(team.data.total)}
              icon={Users}
              caption="Including everybody who reports to them"
            />
            <StatCard
              label="Off today"
              value={String(offToday)}
              icon={CalendarDays}
              caption={offToday === 0 ? 'Everybody is in' : 'On leave or absent'}
            />
          </div>

          <FilterBar
            search={
              <div className="relative">
                <Search
                  className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Search my team"
                  className="ps-9"
                  placeholder="Search by name or employee number"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            }
          >
            <Select
              aria-label="Who to show"
              value={scope}
              onChange={(event) => setScope(event.target.value === 'direct' ? 'direct' : 'all')}
            >
              <option value="all">Everybody in my line</option>
              <option value="direct">Direct reports only</option>
            </Select>
          </FilterBar>

          <Card>
            {rows.length === 0 && (
              <EmptyState
                title="Nobody matches"
                description="Clear the search, or switch back to everybody in your line."
              />
            )}
            {rows.length > 0 && (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-start text-slate-600">
                    <tr>
                      <th className="p-3 font-medium">Person</th>
                      <th className="p-3 font-medium">Reports to</th>
                      <th className="p-3 font-medium">Today</th>
                      <th className="p-3 font-medium">Next away</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((member) => (
                      <tr key={member.employeeId} className="border-b border-slate-100">
                        <td className="p-3">
                          {member.displayName}
                          <span className="block text-xs text-slate-500">
                            {[member.employeeCode, member.designationName]
                              .filter(Boolean).join(' · ')}
                          </span>
                        </td>
                        <td className="p-3 text-slate-600">
                          {member.directReport ? 'You' : 'Somebody in your line'}
                        </td>
                        <td className="p-3">
                          <StatusBadge status={member.todayStatus} />
                          {member.todayNote && (
                            <span className="block max-w-xs text-xs text-slate-500">
                              {member.todayNote}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-slate-600">
                          {member.nextLeaveFrom
                            ? (
                              <>
                                <DateValue value={member.nextLeaveFrom} />
                                {' – '}
                                <DateValue value={member.nextLeaveTo ?? member.nextLeaveFrom} />
                                <span className="block text-xs text-slate-500">
                                  {member.nextLeaveType}
                                </span>
                              </>
                            )
                            : 'Nothing approved'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <p className="flex items-start gap-2 text-xs text-slate-500">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Who reports to whom comes from each person's assignment, so a transfer appears here
            on the day it takes effect. Only approved leave is shown.
          </p>
        </>
      )}
    </div>
  )
}
